import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
  className,
  maxDisplay = 2,
  testId,
  showSelectAll = true,
  searchPlaceholder,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (value) => {
    const newSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const displayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length <= maxDisplay) return selected.join(", ");
    return `${selected.slice(0, maxDisplay).join(", ")} +${selected.length - maxDisplay} more`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-10 justify-between text-left font-normal text-sm w-full",
            selected.length === 0 && "text-slate-400",
            className
          )}
          data-testid={testId}
        >
          <span className="truncate flex-1">{displayText()}</span>
          <div className="flex items-center gap-1 ml-2">
            {selected.length > 0 && (
              <X
                className="h-4 w-4 text-slate-400 hover:text-slate-600"
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="!min-w-[min(450px,calc(100vw-2rem))] p-0 bg-white z-[60] shadow-lg" align="start" sideOffset={5}>
        <div className="p-2 border-b sticky top-0 bg-white z-10">
          <input
            type="text"
            placeholder={searchPlaceholder || `Search ${placeholder}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
            className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-[#006994] focus:border-[#006994]"
          />
        </div>
        <div className="flex items-center justify-between px-2 py-1.5 border-b bg-slate-50">
          {showSelectAll ? (
          <button
            onClick={() => onChange(filteredOptions)}
            className="text-xs text-slate-600 hover:text-[#006994] px-2 py-1 rounded hover:bg-slate-100"
          >
            Select All
          </button>
          ) : <span />}
          {selected.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="text-xs text-slate-600 hover:text-red-500 px-2 py-1 rounded hover:bg-slate-100"
            >
              Clear ({selected.length})
            </button>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto overflow-x-hidden p-1">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              No options found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option}
                onClick={() => handleSelect(option)}
                className={cn(
                  "flex items-start gap-2 px-2 py-2 text-sm rounded cursor-pointer hover:bg-slate-100",
                  selected.includes(option) && "bg-slate-50"
                )}
              >
                <div
                  className={cn(
                    "h-4 w-4 border rounded flex items-center justify-center shrink-0 mt-0.5",
                    selected.includes(option)
                      ? "bg-[#006994] border-[#006994]"
                      : "border-slate-300"
                  )}
                >
                  {selected.includes(option) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <span className="text-sm leading-tight">{option}</span>
              </div>
            ))
          )}
        </div>
        {selected.length > 0 && (
          <div className="p-2 border-t bg-slate-50">
            <div className="flex flex-wrap gap-1">
              {selected.map((item) => (
                <Badge
                  key={item}
                  variant="secondary"
                  className="text-xs cursor-pointer hover:bg-slate-200"
                  onClick={() => handleSelect(item)}
                >
                  {item.length > 15 ? item.substring(0, 15) + "..." : item}
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
